import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import PasswordInput from './PasswordInput';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

// eslint-disable-next-line no-unused-vars
const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  color: white;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #032a4f 0%, #032a4f 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const Message = styled.div`
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  
  ${props => props.type === 'success' ? `
    background-color: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  ` : props.type === 'error' ? `
    background-color: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  ` : `
    background-color: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #6b7280;
`;

const ResetPasswordModal = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [message, setMessage] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setMessage({ type: 'error', text: t('resetPasswordModal.invalidLink') });
      setVerifying(false);
      return;
    }

    // Verify token
    const verifyToken = async () => {
      try {
        console.log('🔒 Verifying token:', token);
        const response = await api.get(`/password-reset/verify-reset-token/${token}`);
        
        console.log('🔒 Token verification response:', response);
        
        if (response && response.success) {
          setTeamInfo(response.data);
          const name = response.data.displayName || response.data.teamName;
          setMessage({
            type: 'success',
            text: response.data.accountType === 'player'
              ? t('resetPasswordModal.resetForAccount', { name })
              : t('resetPasswordModal.resetForTeam', { teamName: name }),
          });
        } else {
          setMessage({ type: 'error', text: t('resetPasswordModal.invalidToken') });
        }
      } catch (error) {
        console.error('Token verification error:', error);
        console.error('Error response:', error.response);
        setMessage({ type: 'error', text: t('resetPasswordModal.invalidToken') });
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: t('resetPasswordModal.fillAllFields') });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: t('resetPasswordModal.passwordMin6') });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: t('resetPasswordModal.passwordsNoMatch') });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      console.log('🔒 Resetting password with token:', token);
      const response = await api.post('/password-reset/reset-password', {
        token,
        newPassword
      });
      
      console.log('🔒 Reset password response:', response);
      
      if (response && response.success) {
        setMessage({
          type: 'success',
          text: t('resetPasswordModal.resetSuccess'),
        });

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setMessage({ type: 'error', text: response?.message || t('resetPasswordModal.genericError') });
      }
    } catch (error) {
      console.error('Reset password error:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || t('resetPasswordModal.resetFailedFallback');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <ModalOverlay>
        <ModalContent>
          <LoadingContainer>{t('resetPasswordModal.verifyingToken')}</LoadingContainer>
        </ModalContent>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{t('resetPasswordModal.title')}</ModalTitle>
        </ModalHeader>

        {message && (
          <Message type={message.type}>
            {message.text}
          </Message>
        )}

        {teamInfo && (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="newPassword">{t('resetPasswordModal.newPassword')}</Label>
              <PasswordInput
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('resetPasswordModal.placeholderNew')}
                required
                disabled={loading}
                minLength={6}
                padding="0.75rem"
                paddingRight="2.5rem"
                border="1px solid #d1d5db"
                borderRadius="8px"
                fontSize="0.875rem"
                focusBorderColor="#043663"
                focusBoxShadow="0 0 0 3px rgba(4, 54, 99, 0.1)"
                autoComplete="new-password"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirmPassword">{t('resetPasswordModal.confirmPassword')}</Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('resetPasswordModal.placeholderConfirm')}
                required
                disabled={loading}
                minLength={6}
                padding="0.75rem"
                paddingRight="2.5rem"
                border="1px solid #d1d5db"
                borderRadius="8px"
                fontSize="0.875rem"
                focusBorderColor="#043663"
                focusBoxShadow="0 0 0 3px rgba(4, 54, 99, 0.1)"
                autoComplete="new-password"
              />
            </FormGroup>

            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
              {t('resetPasswordModal.passwordHint')}
            </p>

            <Button type="submit" disabled={loading}>
              {loading ? t('resetPasswordModal.submitting') : t('resetPasswordModal.submit')}
            </Button>
          </Form>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default ResetPasswordModal;
